import {
  MACROS,
  NUTRIENT_COLORS,
  NUTRIENT_LABELS,
  NUTRIENT_UNITS,
  formatNutrient,
  type Macro,
} from '~/data/nutrition'

/** What a gram of each macro is worth in calories, for the share each takes of the plate. */
const MACRO_CALORIES: Record<Macro, number> = { protein: 4, carbs: 4, fat: 9 }

/** How long the bar takes to draw itself, matching the phone's. */
const DRAW_MS = 520

/**
 * What a day comes to and what it is made of, as the phone draws it: one line cut into the
 * three macros' shares of the calories, and the figures under it.
 *
 * The line is one line divided, not a row of bars with air between them. What it says is how
 * the shares stand against one another, and a gap between two of them belongs to neither:
 * read across, separated bars are amounts, and one line cut in three is a ratio.
 *
 * The width is cut once: the calories hold the left edge and the three macros stand together
 * against the right. The cut falls there because that is where the meaning is — the calories
 * are what the day comes to and the macros are what they are made of, and a row of four evenly
 * spread says the four are four of a kind. Without the calories the three are three of a kind,
 * and share the width evenly.
 */
export function MacroPlate({
  calories,
  protein,
  carbs,
  fat,
  /**
   * Whether the calories stand on the plate at all. The weekly check-in already says them
   * large over it, and a number said twice on one screen is a number saying nothing the
   * second time.
   */
  caloriesShown = true,
  /** False until the page has landed, so the bar draws itself rather than arriving drawn. */
  arrived = true,
}: {
  calories?: number | null
  protein: number | null
  carbs: number | null
  fat: number | null
  caloriesShown?: boolean
  arrived?: boolean
}) {
  const grams: Record<Macro, number> = { protein: protein ?? 0, carbs: carbs ?? 0, fat: fat ?? 0 }
  const whole = MACROS.reduce((sum, name) => sum + grams[name] * MACRO_CALORIES[name], 0)
  const target: Record<Macro, number | null> = { protein, carbs, fat }

  /** One of the plate's four: what it comes to, over its own name in its own colour. */
  const figure = (name: Macro | 'calories') => {
    const held = name === 'calories' ? (calories ?? null) : target[name]
    return (
      <div key={name} className="flex flex-col gap-0.5">
        {/*
          The figure alone. Four of these share the width, and a unit written beside each of
          them is a unit with nowhere to go — what it is measured in is said in the name under
          it, and in full to anything reading the screen.
        */}
        <span className="text-lg font-medium tabular-nums">
          {held === null ? '—' : formatNutrient(name, held)}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-ink-muted">
          {/*
            The dot is the line's key: it says "this colour, up there, is this figure".
            Calories have no run of the line — they are what the three macros come to — so they
            wear no dot, and the word under them is the unit rather than the name.
          */}
          {name !== 'calories' && (
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{ background: NUTRIENT_COLORS[name] }}
            />
          )}
          {name === 'calories' ? NUTRIENT_UNITS.calories : NUTRIENT_LABELS[name]}
        </span>
      </div>
    )
  }

  return (
    <div aria-label="A day" role="group" className="flex w-full flex-col gap-3">
      {/* The line says the ratio, and says it whole rather than as three loose widths. */}
      <div
        role="img"
        aria-label={MACROS.map(
          (name) =>
            `${NUTRIENT_LABELS[name]} ${target[name] === null ? 'not set' : formatNutrient(name, target[name])}`,
        ).join(', ')}
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-hover"
      >
        {whole > 0 &&
          MACROS.map((name) => (
            <span
              key={name}
              className="transition-[width] ease-out"
              style={{
                width: arrived
                  ? `${String((grams[name] * MACRO_CALORIES[name] * 100) / whole)}%`
                  : '0%',
                background: NUTRIENT_COLORS[name],
                transitionDuration: `${String(DRAW_MS)}ms`,
              }}
            />
          ))}
      </div>
      {caloriesShown ? (
        <div className="flex w-full items-start">
          {figure('calories')}
          <span className="flex-1" />
          <div className="flex gap-5">{MACROS.map((name) => figure(name))}</div>
        </div>
      ) : (
        <div className="flex w-full justify-between">{MACROS.map((name) => figure(name))}</div>
      )}
    </div>
  )
}
